import React, { useState } from 'react'
import { Tent, CheckCircle } from 'lucide-react'

const EVENT_TYPES = ['wedding', 'corporate', 'birthday', 'festival', 'community', 'other']
const SOURCES = ['google', 'facebook', 'referral', 'returning', 'other']

export default function PublicIntakeForm() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    event_date: '', event_type: 'wedding',
    guest_count: '', notes: '', source: 'google',
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  function set(field, val) { setForm(f => ({ ...f, [field]: val })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please provide your name and email.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? parseInt(form.guest_count, 10) : null,
      }

      const intakeToken = process.env.REACT_APP_INBOUND_LEAD_TOKEN || ''
      const headers = { 'Content-Type': 'application/json' }
      if (intakeToken) headers['x-intake-token'] = intakeToken

      const response = await fetch('/api/inbound-lead', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })

      let data = null
      try {
        data = await response.json()
      } catch (_) {
        data = null
      }

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || 'Submission failed')
      }

      setSubmitted(true)
    } catch (_) {
      setError('Something went wrong. Please try again or call us directly.')
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="intake-wrap">
        <div className="intake-card">
          <div className="intake-success">
            <CheckCircle size={52} className="success-icon" />
            <h2>We've received your inquiry!</h2>
            <p>
              Thank you, <strong>{form.name}</strong>. A member of our team will be in touch
              within 24 hours to discuss your event.
            </p>
            <div className="alert alert-info" style={{ textAlign: 'left' }}>
              If you need to reach us sooner, please call or email directly.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="intake-wrap">
      <div className="intake-card">
        {/* Header */}
        <div className="intake-header">
          <div className="intake-logo">
            <Tent size={24} />
          </div>
          <div>
            <h1 className="intake-title">Tent Rental Inquiry</h1>
            <p className="intake-sub">Winnipeg Tent Rental — tell us about your event</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="intake-form">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Your Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" required />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="204-000-0000" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" required />
          </div>

          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Event Date</label>
              <input type="date" className="form-input" value={form.event_date} onChange={e => set('event_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select className="form-select" value={form.event_type} onChange={e => set('event_type', e.target.value)}>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Approximate Guest Count</label>
            <input type="number" className="form-input" value={form.guest_count} onChange={e => set('guest_count', e.target.value)} placeholder="100" min={0} style={{ maxWidth: 200 }} />
          </div>

          <div className="form-group">
            <label className="form-label">Tell us about your tent needs</label>
            <textarea
              className="form-textarea"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={4}
              placeholder="Describe the size, number of tents, specific requirements, venue details, or anything else that would help us prepare an accurate quote…"
            />
          </div>

          <div className="form-group">
            <label className="form-label">How did you hear about us?</label>
            <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)} style={{ maxWidth: 240 }}>
              {SOURCES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>

          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting} style={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Submitting…' : 'Submit Inquiry'}
          </button>
        </form>
      </div>

      <style>{`
        .intake-wrap {
          min-height: 100vh;
          background: var(--color-bg);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 40px 16px 60px;
        }
        .intake-card {
          width: 100%;
          max-width: 580px;
          background: white;
          border-radius: var(--radius-xl);
          padding: 36px;
          box-shadow: var(--shadow-md);
        }
        .intake-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 28px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--color-border);
        }
        .intake-logo {
          width: 48px;
          height: 48px;
          background: var(--color-navy);
          color: var(--color-green-light);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .intake-title {
          font-size: var(--text-xl);
          font-weight: 700;
        }
        .intake-sub {
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          margin-top: 2px;
        }
        .intake-form {
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
        }
        .intake-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          text-align: center;
          padding: 20px 0;
        }
        .success-icon { color: var(--color-green); }
        .intake-success h2 { font-size: var(--text-xl); font-weight: 700; }
        .intake-success p { color: var(--color-text-secondary); line-height: 1.6; }
      `}</style>
    </div>
  )
}
